import React from 'react'
import { Card, CardContent, CardHeader } from '../ui'
import { Button } from '../ui/Button'
import { useI18n } from '../../i18n'
import LifecycleBadge from './LifecycleBadge'
import { type BulkAssignAssetRow } from './BulkAssignAssetsModal'
import InformationSystemAssetsSection from './InformationSystemAssetsSection'
import type { InformationSystemDetail } from '../../services/informationSystemsApi'
import { LIFECYCLE_OPTIONS } from './informationSystemConstants'
import InformationSystemGrcSections from './InformationSystemGrcSections'
import InformationSystemGraphView from './InformationSystemGraphView'
import InformationSystemCompletenessChecklist from './InformationSystemCompletenessChecklist'
import OperationalTrendWidget from './OperationalTrendWidget'
import ReadinessBlockersPanel from './ReadinessBlockersPanel'
import {
  type PassportSection,
  passportSectionElementId,
} from './informationSystemPassportTypes'
import isStyles from '../../styles/components/assets/AssetsInformationSystems.module.scss'

function displayValue(value: string | null | undefined, unknown: string) {
  if (value == null || String(value).trim() === '') return unknown
  return value
}

const NAV_SECTIONS: PassportSection[] = [
  'overview',
  'assets',
  'data',
  'compliance',
  'controls',
  'evidence',
  'remediation',
  'readiness',
  'graph',
]

const NAV_LABELS: Record<PassportSection, { key: string; fallback: string }> = {
  overview: { key: 'assets.passport.tabOverview', fallback: 'Overview' },
  assets: { key: 'assets.passport.assets', fallback: 'Assets' },
  data: { key: 'assets.passport.data', fallback: 'Data' },
  compliance: { key: 'assets.grc.complianceScope', fallback: 'Compliance' },
  controls: { key: 'assets.grc.controls', fallback: 'Controls' },
  evidence: { key: 'assets.grc.evidence', fallback: 'Evidence' },
  remediation: { key: 'assets.grc.remediation', fallback: 'Remediation' },
  readiness: { key: 'assets.postureRow.openReadiness', fallback: 'Readiness' },
  graph: { key: 'assets.passport.tabGraph', fallback: 'Graph' },
}

interface Props {
  system: InformationSystemDetail
  inventoryAssets: BulkAssignAssetRow[]
  onEdit: () => void
  onDelete: () => void
  onArchive: () => void
  onLifecycleChange: (status: string) => Promise<void>
  onLinkAsset: (assetId: string) => Promise<void>
  onUnlinkAsset: (assetId: string) => Promise<void>
  onBulkLink: (assetIds: string[]) => Promise<{ linked?: Array<string | number>; skipped?: Array<string | number>; errors?: Array<{ assetId: string | number; message: string }> }>
  onBulkUnlink: (assetIds: string[]) => Promise<{ unlinked?: Array<string | number>; errors?: Array<{ assetId: string | number; message: string }> }>
  linking?: boolean
  initialSection?: PassportSection
  embedded?: boolean
  focusedSection?: PassportSection
  hideSectionNav?: boolean
}

export default function InformationSystemPassport({
  system,
  inventoryAssets,
  onEdit,
  onDelete,
  onArchive,
  onLifecycleChange,
  onLinkAsset,
  onUnlinkAsset,
  onBulkLink,
  onBulkUnlink,
  linking,
  initialSection = 'overview',
  embedded = false,
  focusedSection,
  hideSectionNav = false,
}: Props) {
  const { t } = useI18n()
  const unknown = t('common.unknown', 'Unknown')
  const notAvailable = t('assets.riskCompliance.notAvailable', 'Not available yet')
  const [lifecycle, setLifecycle] = React.useState(system.lifecycleStatus || 'DRAFT')
  const [activeSection, setActiveSection] = React.useState<PassportSection>(initialSection)
  const scrollRootRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    setActiveSection(initialSection)
  }, [system.id, initialSection])

  React.useEffect(() => {
    setLifecycle(system.lifecycleStatus || 'DRAFT')
  }, [system.id, system.lifecycleStatus])

  const scrollToSection = React.useCallback((section: PassportSection) => {
    if (section === 'graph') return
    const root = scrollRootRef.current
    const targetId = passportSectionElementId(section)
    const node = root?.querySelector<HTMLElement>(`#${targetId}`) ?? document.getElementById(targetId)
    node?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  React.useEffect(() => {
    if (activeSection === 'graph') return
    const timer = window.setTimeout(() => scrollToSection(activeSection), 50)
    return () => window.clearTimeout(timer)
  }, [activeSection, system.id, scrollToSection])

  const goToSection = (section: PassportSection) => {
    setActiveSection(section)
    if (section !== 'graph') {
      window.setTimeout(() => scrollToSection(section), 0)
    }
  }

  const sectionNav = (
    <nav className="flex gap-1 overflow-x-auto pb-2 border-b border-gray-100 -mx-1 px-1" aria-label={t('assets.passport.sectionNav', 'Passport sections')}>
      {NAV_SECTIONS.map((id) => (
        <button
          key={id}
          type="button"
          className={`text-xs px-2.5 py-1 rounded whitespace-nowrap shrink-0 ${
            activeSection === id ? 'bg-blue-100 text-blue-800 font-medium' : 'text-gray-600 hover:bg-gray-50'
          }`}
          onClick={() => goToSection(id)}
          aria-current={activeSection === id ? 'page' : undefined}
        >
          {t(NAV_LABELS[id].key, NAV_LABELS[id].fallback)}
        </button>
      ))}
    </nav>
  )

  const actionBar = (
    <div className={embedded ? isStyles.passportActionBar : 'flex flex-wrap gap-2'}>
      <Button type="button" size="small" color="outline" onClick={onEdit}>
        {t('common.edit', 'Edit')}
      </Button>
      <Button type="button" size="small" color="outline" onClick={onArchive}>
        {t('assets.lifecycle.archive', 'Archive')}
      </Button>
      <Button type="button" size="small" color="danger" onClick={onDelete}>
        {t('common.delete', 'Delete')}
      </Button>
    </div>
  )

  const showSection = (section: PassportSection) => !focusedSection || focusedSection === section

  const detailsBody = (
    <div ref={scrollRootRef} className="space-y-6">
      {showSection('readiness') && !focusedSection ? (
      <section id={passportSectionElementId('readiness')} className="scroll-mt-4 space-y-4">
        <InformationSystemCompletenessChecklist systemId={system.id} />
        <OperationalTrendWidget systemId={system.id} />
        <ReadinessBlockersPanel systemId={system.id} />
      </section>
      ) : null}

      {showSection('overview') ? (
      <section
        id={passportSectionElementId('overview')}
        className={`scroll-mt-4 ${embedded ? isStyles.passportOverviewSection : ''}`}
      >
        <h4 className={embedded ? isStyles.passportSectionHeading : 'font-medium mb-2'}>
          {t('assets.passport.overview', 'Overview')}
        </h4>
        <div className={embedded ? isStyles.passportLifecycleRow : 'flex flex-wrap items-center gap-2 mb-3'}>
          <label className={embedded ? isStyles.passportLifecycleLabel : 'text-sm text-gray-600'}>
            {t('assets.lifecycle.status', 'Lifecycle')}
          </label>
          <select
            className={embedded ? isStyles.passportLifecycleSelect : 'border rounded px-2 py-1 text-sm'}
            value={lifecycle || 'DRAFT'}
            onChange={async (e) => {
              const v = e.target.value
              setLifecycle(v)
              await onLifecycleChange(v)
            }}
          >
            {LIFECYCLE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(`assets.lifecycle.${s}`, s)}
              </option>
            ))}
          </select>
        </div>
        <dl className={embedded ? isStyles.passportFieldGrid : 'grid gap-2 sm:grid-cols-2 text-sm'}>
          <div className={embedded ? isStyles.passportField : undefined}>
            <dt className={embedded ? isStyles.passportFieldLabel : 'text-gray-500'}>
              {t('assets.informationSystems.fields.description', 'Description')}
            </dt>
            <dd className={embedded ? isStyles.passportFieldValue : undefined}>{displayValue(system.description, unknown)}</dd>
          </div>
          <div className={embedded ? isStyles.passportField : undefined}>
            <dt className={embedded ? isStyles.passportFieldLabel : 'text-gray-500'}>
              {t('assets.informationSystems.fields.businessOwner', 'Business owner')}
            </dt>
            <dd className={embedded ? isStyles.passportFieldValue : undefined}>
              {displayValue(system.businessOwnerDisplay || system.businessOwner, unknown)}
            </dd>
          </div>
          <div className={embedded ? isStyles.passportField : undefined}>
            <dt className={embedded ? isStyles.passportFieldLabel : 'text-gray-500'}>
              {t('assets.informationSystems.fields.technicalOwner', 'Technical owner')}
            </dt>
            <dd className={embedded ? isStyles.passportFieldValue : undefined}>
              {displayValue(system.technicalOwnerDisplay || system.technicalOwner, unknown)}
            </dd>
          </div>
          <div className={embedded ? isStyles.passportField : undefined}>
            <dt className={embedded ? isStyles.passportFieldLabel : 'text-gray-500'}>
              {t('assets.informationSystems.fields.environment', 'Environment')}
            </dt>
            <dd className={embedded ? isStyles.passportFieldValue : undefined}>{displayValue(system.environment, unknown)}</dd>
          </div>
          <div className={embedded ? isStyles.passportField : undefined}>
            <dt className={embedded ? isStyles.passportFieldLabel : 'text-gray-500'}>
              {t('assets.informationSystems.fields.criticality', 'Criticality')}
            </dt>
            <dd className={embedded ? isStyles.passportFieldValue : undefined}>{displayValue(system.criticality, unknown)}</dd>
          </div>
        </dl>
        {(system.hints || []).length > 0 ? (
          <div className="mt-4">
            <h5 className={embedded ? isStyles.passportSubheading : 'text-sm font-medium mb-2'}>
              {t('assets.passport.hints', 'Relevance hints')}
            </h5>
            <ul className={embedded ? isStyles.passportHintList : 'space-y-2'}>
              {(system.hints || []).map((h) => (
                <li key={h.id} className={embedded ? isStyles.passportHintItem : 'text-sm border-l-4 border-amber-400 pl-3 py-1 bg-amber-50/50 rounded-r'}>
                  <span className={embedded ? isStyles.passportHintTitle : 'font-medium'}>
                    {t(`assets.hints.${h.id}.label`, h.label)}
                  </span>
                  <p className={embedded ? isStyles.passportHintBody : 'text-gray-600 mt-0.5'}>
                    {t(`assets.hints.${h.id}.reason`, h.reason)}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="mt-4">
          <h5 className={embedded ? isStyles.passportSubheading : 'text-sm font-medium mb-2'}>
            {t('assets.passport.riskCompliance', 'Risk & Compliance preview')}
          </h5>
          <dl className={embedded ? isStyles.passportFieldGrid : 'grid gap-2 sm:grid-cols-2 text-sm'}>
            <div>
              <dt className="text-gray-500">{t('assets.riskCompliance.riskScore', 'Risk score')}</dt>
              <dd className="italic text-gray-400">{notAvailable}</dd>
            </div>
            <div>
              <dt className="text-gray-500">{t('assets.riskCompliance.complianceScore', 'Compliance score')}</dt>
              <dd className="italic text-gray-400">{notAvailable}</dd>
            </div>
            <div>
              <dt className="text-gray-500">{t('assets.riskCompliance.vulnerabilities', 'Vulnerabilities')}</dt>
              <dd>
                {system.riskCompliancePreview?.vulnerabilityCount != null
                  ? String(system.riskCompliancePreview.vulnerabilityCount)
                  : unknown}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">{t('assets.riskCompliance.evidenceCoverage', 'Evidence coverage')}</dt>
              <dd className="italic text-gray-400">{notAvailable}</dd>
            </div>
          </dl>
          <p className="text-xs text-gray-500 mt-2">{system.riskCompliancePreview?.evidenceCoverageNote}</p>
        </div>
        <div className={embedded ? isStyles.passportAuditMeta : 'text-xs text-gray-500 border-t pt-3 mt-4'}>
          <p>
            {t('assets.passport.auditCreated', 'Created')}: {system.createdAt ? new Date(system.createdAt).toLocaleString() : unknown}
          </p>
          <p>
            {t('assets.passport.auditUpdated', 'Updated')}: {system.updatedAt ? new Date(system.updatedAt).toLocaleString() : unknown}
          </p>
        </div>
      </section>
      ) : null}

      {showSection('assets') ? (
      <section id={passportSectionElementId('assets')} className="scroll-mt-4">
        <InformationSystemAssetsSection
          system={system}
          inventoryAssets={inventoryAssets}
          onLinkAsset={onLinkAsset}
          onUnlinkAsset={onUnlinkAsset}
          onBulkLink={onBulkLink}
          onBulkUnlink={onBulkUnlink}
          linking={linking}
        />
      </section>
      ) : null}

      {showSection('data') ? (
      <section id={passportSectionElementId('data')} className="scroll-mt-4">
        <h4 className="font-medium mb-2">{t('assets.passport.data', 'Data')} ({system.dataCategoriesCount})</h4>
        {(system.dataCategories || []).length === 0 ? (
          <p className="text-sm text-gray-500">{t('assets.dataMap.noCategoriesForSystem', 'No categories.')}</p>
        ) : (
          <ul className="text-sm space-y-2">
            {(system.dataCategories || []).map((dc) => (
              <li key={dc.id} className="border rounded p-2">
                <div className="font-medium">{dc.category}</div>
                {dc.classification ? <div className="text-gray-500">{dc.classification}</div> : null}
                {dc.processingPurpose ? <div className="text-gray-600">{dc.processingPurpose}</div> : null}
                {dc.storageLocation ? <div className="text-gray-600">{t('assets.dataMap.storageLocation', 'Storage')}: {dc.storageLocation}</div> : null}
                {dc.retentionPeriod ? <div className="text-gray-600">{t('assets.dataMap.retentionPeriod', 'Retention')}: {dc.retentionPeriod}</div> : null}
                {dc.crossBorderTransfer ? <div className="text-amber-700">{t('assets.dataMap.crossBorder', 'Cross-border transfer')}</div> : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      ) : null}

      {!focusedSection ? <InformationSystemGrcSections systemId={system.id} /> : null}
    </div>
  )

  const inner = (
    <>
      {!hideSectionNav ? sectionNav : null}
      {!embedded ? <div className="mt-3">{actionBar}</div> : null}
      {(focusedSection === 'graph' || activeSection === 'graph') ? (
        <div id={passportSectionElementId('graph')} className="mt-4">
          <InformationSystemGraphView systemId={system.id} />
        </div>
      ) : (
        <div className="mt-4">{detailsBody}</div>
      )}
      {embedded ? actionBar : null}
    </>
  )

  return (
    <>
      {embedded ? (
        <div className={isStyles.passportEmbeddedRoot}>{inner}</div>
      ) : (
        <Card className="bg-white border border-gray-100">
          <CardHeader className="flex flex-wrap justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl font-semibold">{system.name}</h3>
                <LifecycleBadge status={system.lifecycleStatus} />
              </div>
              {system.code ? <p className="text-sm text-gray-500">{system.code}</p> : null}
            </div>
          </CardHeader>
          <CardContent>{inner}</CardContent>
        </Card>
      )}

    </>
  )
}

export type { PassportSection } from './informationSystemPassportTypes'
